// ============================================
// VOLUNTEER MODULE - DUTY ASSIGNMENT
// BUG FIX: State persists from DB on load
// AND/OR filter logic
// Column sorting
// Bulk assignment + auto-save
// ============================================

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useVolunteerData } from "../components/VolunteerDataContext";
import { useTheme } from "../components/ThemeContext";
import {
  getEvents, getVenuesByEvent, getGatesByVenue,
  getVolunteers, saveAssignment, getAssignmentsByEvent,
  sendWhatsAppToVolunteer, sendWhatsAppBulk,
  sendSMSToVolunteer, sendSMSBulk,
} from "../services/api";

// ── State persistence strategy ────────────────
// We store volunteer→assignment in local state.
// On mount we load saved assignments from DB.
// Venue is matched by venue_id (primary) or
// venue_name (fallback for older records).
// Gates are pre-loaded for all saved venues.

const sColor = (s,C) => ({ Active:C.green, Draft:C.orange, Completed:C.accent, Cancelled:C.red }[s]||C.muted);
const tColor = (t,C) => ({ Conference:C.accent, Festival:C.orange, Sports:C.green, Religious:C.purple, Music:C.red }[t]||C.muted);
const regColor = (s,C) => ({ arrived:C.green, approved:C.green, pending:C.orange, not_coming:C.red, rejected:C.red }[s]||C.muted);
const regLabel = (s) => ({ arrived:"Arrived", pending:"Pending", not_coming:"Not Coming", approved:"Approved", rejected:"Rejected" }[s]||s||"pending");

const Badge = ({ text, color }) => (
  <span className="vh-badge" style={{ background:`${color}22`, color, border:`1px solid ${color}44` }}>{text}</span>
);
const Msg = ({ msg, C }) => !msg ? null : (
  <div style={{ padding:"11px 16px", borderRadius:10, marginBottom:14,
    background:msg.type==="success"?C.greenSoft:C.redSoft,
    border:`1px solid ${msg.type==="success"?C.green:C.red}44`,
    color:msg.type==="success"?C.green:C.red, fontSize:13, fontWeight:600 }}>
    {msg.text}
  </div>
);

// ============================================
// DYNAMIC FILTER BUILDER
// Supports AND / OR logic between rows
// ============================================
function FilterBuilder({ fields, rows, onChange, logic, onLogicChange, C }) {
  const addRow    = () => onChange([...rows, { id:Date.now(), field:fields[0]?.key||"", value:"" }]);
  const removeRow = (id) => onChange(rows.filter(r=>r.id!==id));
  const updateRow = (id, k, v) => onChange(rows.map(r=>r.id===id?{...r,[k]:v}:r));
  const inp = { padding:"7px 10px", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:7, color:C.text, fontSize:12, outline:"none" };
  return (
    <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:11, padding:14, marginBottom:12 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.accent, textTransform:"uppercase", letterSpacing:"0.5px" }}>
            Field Filters
            {rows.length>0&&<span style={{ background:C.accent, color:"#000", borderRadius:99, padding:"1px 8px", fontSize:10, marginLeft:8, fontWeight:800 }}>{rows.length}</span>}
          </div>
          {rows.length>1 && (
            <div style={{ display:"flex", gap:4, background:C.card, padding:3, borderRadius:8, border:`1px solid ${C.border}` }}>
              {["AND","OR"].map(l=>(
                <button key={l} onClick={()=>onLogicChange(l)}
                  style={{ padding:"3px 10px", background:logic===l?C.accent:"transparent", border:"none", borderRadius:6, color:logic===l?"#000":C.muted, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                  {l}
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{ display:"flex", gap:6 }}>
          {rows.length>0&&<button onClick={()=>onChange([])} style={{ padding:"4px 10px", background:C.redSoft, border:`1px solid ${C.red}33`, borderRadius:6, color:C.red, fontSize:11, fontWeight:600, cursor:"pointer" }}>Clear All</button>}
          <button onClick={addRow} style={{ padding:"4px 12px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:6, color:C.accent, fontSize:11, fontWeight:700, cursor:"pointer" }}>+ Add Filter</button>
        </div>
      </div>
      {rows.length===0&&<div style={{ fontSize:12, color:C.muted, fontStyle:"italic", textAlign:"center", padding:"6px 0" }}>Click "+ Add Filter" to filter by any field</div>}
      {rows.map((row, idx)=>{
        const fd = fields.find(f=>f.key===row.field)||fields[0]||{};
        return (
          <div key={row.id}>
            {idx>0 && (
              <div style={{ textAlign:"center", fontSize:11, fontWeight:700, color:C.accent, margin:"4px 0",
                padding:"2px 8px", background:`${C.accent}22`, borderRadius:6, display:"inline-block", marginLeft:"50%" }}>
                {logic}
              </div>
            )}
            <div style={{ display:"flex", gap:8, marginBottom:8, alignItems:"center", flexWrap:"wrap" }}>
              <select value={row.field} onChange={e=>updateRow(row.id,"field",e.target.value)} style={{ ...inp, flex:"0 0 170px", cursor:"pointer" }}>
                {fields.map(f=><option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
              {fd.type==="select"?(
                <select value={row.value} onChange={e=>updateRow(row.id,"value",e.target.value)} style={{ ...inp, flex:1, minWidth:130, cursor:"pointer" }}>
                  <option value="">Any</option>
                  {(fd.options||[]).map(o=><option key={o}>{o}</option>)}
                </select>
              ):fd.type==="date"?(
                <input type="date" value={row.value} onChange={e=>updateRow(row.id,"value",e.target.value)} style={{ ...inp, flex:1, minWidth:130 }} />
              ):(
                <input type="text" value={row.value} onChange={e=>updateRow(row.id,"value",e.target.value)} placeholder={`Filter by ${fd.label}...`} style={{ ...inp, flex:1, minWidth:130 }} />
              )}
              <button onClick={()=>removeRow(row.id)} style={{ padding:"6px 10px", background:C.redSoft, border:`1px solid ${C.red}33`, borderRadius:7, color:C.red, fontSize:14, fontWeight:700, cursor:"pointer", flexShrink:0 }}>x</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function applyFilters(items, rows, logic, getVal) {
  if(!rows||!rows.length) return items;
  return items.filter(item => {
    const results = rows.map(row => {
      if(!row.value) return true;
      return String(getVal(item,row.field)||"").toLowerCase().includes(row.value.toLowerCase());
    });
    return logic==="OR" ? results.some(Boolean) : results.every(Boolean);
  });
}

// ============================================
// EVENT LIST
// ============================================
function EventList({ onSelect, C }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortCol, setSortCol] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    setLoading(true);
    getEvents().then(r=>setEvents(r.data.events||[])).catch(()=>setEvents([])).finally(()=>setLoading(false));
  }, []);

  const toggleSort = (col) => {
    if(sortCol===col) setSortDir(d=>d==="asc"?"desc":"asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  const sorted = [...events].sort((a,b)=>{
    let va="",vb="";
    if(sortCol==="name")   { va=a.name||""; vb=b.name||""; }
    if(sortCol==="type")   { va=a.eventType||""; vb=b.eventType||""; }
    if(sortCol==="date")   { va=a.startDate||""; vb=b.startDate||""; }
    if(sortCol==="status") { va=a.status||""; vb=b.status||""; }
    return sortDir==="asc"?va.localeCompare(vb):vb.localeCompare(va);
  });

  const SH = ({ col, label }) => (
    <div onClick={()=>toggleSort(col)} style={{ cursor:"pointer", userSelect:"none", display:"flex", alignItems:"center", gap:4 }}>
      {label}
      <span style={{ fontSize:9, color:sortCol===col?C.accent:C.border }}>
        {sortCol===col?(sortDir==="asc"?"▲":"▼"):"▲"}
      </span>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom:22 }}>
        <div style={{ fontSize:22, fontWeight:800, color:C.text, fontFamily:"Georgia, serif" }}>Assign Duties</div>
        <div style={{ fontSize:13, color:C.muted, marginTop:4 }}>Select an event to assign volunteers to venues and gates</div>
      </div>
      {loading ? <div style={{ padding:40, textAlign:"center", color:C.muted }}>Loading events...</div>
      : events.length===0 ? (
        <div style={{ padding:60, textAlign:"center", background:C.card, border:`1px solid ${C.border}`, borderRadius:16 }}>
          <div style={{ fontSize:40, marginBottom:14 }}>📅</div>
          <div style={{ fontSize:15, fontWeight:700, color:C.text }}>No events found</div>
        </div>
      ) : (
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, overflow:"hidden" }}>
          <div style={{ padding:"10px 16px", borderBottom:`1px solid ${C.border}`, fontSize:11, color:C.muted }}>
            Click column headers to sort
          </div>
          <div className="responsive-table">
            <table>
              <thead>
                <tr style={{ background:C.bg }}>
                  {[{col:"name",label:"Event"},{col:"type",label:"Type"},{col:"date",label:"Start Date"},{col:"status",label:"Status"}].map(({col,label})=>(
                    <th key={col} style={{ padding:"11px 14px", textAlign:"left", fontSize:10, fontWeight:700, color:sortCol===col?C.accent:C.muted, letterSpacing:"0.8px", textTransform:"uppercase", borderBottom:`1px solid ${C.border}`, whiteSpace:"nowrap" }}>
                      <SH col={col} label={label} />
                    </th>
                  ))}
                  <th style={{ padding:"11px 14px", textAlign:"left", fontSize:10, fontWeight:700, color:C.muted, letterSpacing:"0.8px", textTransform:"uppercase", borderBottom:`1px solid ${C.border}` }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((ev,i)=>(
                  <tr key={i} style={{ borderBottom:`1px solid ${C.border}` }}
                    onMouseEnter={e=>e.currentTarget.style.background=C.border+"44"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <td style={{ padding:"13px 14px", fontSize:13, fontWeight:700, color:C.text }}>{ev.name}</td>
                    <td style={{ padding:"13px 14px" }}><Badge text={ev.eventType||"—"} color={tColor(ev.eventType,C)} /></td>
                    <td style={{ padding:"13px 14px", fontSize:12, color:C.muted }}>{ev.startDate||"—"}</td>
                    <td style={{ padding:"13px 14px" }}><Badge text={ev.status} color={sColor(ev.status,C)} /></td>
                    <td style={{ padding:"13px 14px" }}>
                      <button onClick={()=>onSelect(ev)} style={{ padding:"8px 18px", background:C.green, border:"none", borderRadius:9, color:"#000", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                        Assign Duties
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// ASSIGN VIEW
// ============================================
function AssignView({ event, onBack, C }) {
  const [volunteers, setVols]       = useState([]);
  const [venues, setVenues]         = useState([]);
  const [gateMap, setGateMap]       = useState({});
  const [assignments, setAssign]    = useState({});  // { volunteerId: { venueId, venueName, gateId, gateName } }
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState({});
  const [autoSaved, setAutoSaved]   = useState({});
  const [msg, setMsg]               = useState(null);
  const [filter, setFilter]         = useState("All");
  const [date, setDate]             = useState(new Date().toISOString().split("T")[0]);
  const [search, setSearch]         = useState("");
  const [sortCol, setSortCol]       = useState("fullName");
  const [sortDir, setSortDir]       = useState("asc");
  const [bulkSending, setBulkSending]       = useState(false);
  const [bulkSmsSending, setBulkSmsSending] = useState(false);
  const [selected, setSelected]             = useState(new Set());
  const [showBulk, setShowBulk]             = useState(false);
  const [bulkVenue, setBulkVenue]           = useState("");
  const [bulkGate, setBulkGate]             = useState("");
  const [bulkGates, setBulkGates]           = useState([]);
  const [bulkAssigning, setBulkAssigning]   = useState(false);
  const [filterRows, setFilterRows]         = useState([]);
  const [filterLogic, setFilterLogic]       = useState("AND");
  const [showFilters, setShowFilters]       = useState(false);
  const autoSaveTimers = useRef({});
  const { version }    = useVolunteerData(); // re-load if volunteers change elsewhere

  // ── STATE PERSISTENCE: Load saved assignments from DB on mount ─────
  //
  // Strategy:
  // 1. Load volunteers + venues + saved assignments in parallel
  // 2. For each saved assignment, restore { venueId, venueName, gateId, gateName }
  // 3. venue_id match (primary) → exact venue dropdown match
  //    venue_name match (fallback) → for older records without venue_id
  // 4. Pre-load gates for ALL venues that appear in saved assignments
  //    so dropdowns are populated before the user opens them
  //
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [volRes, venRes, assignRes] = await Promise.all([
        getVolunteers(),
        getVenuesByEvent(event.id),
        getAssignmentsByEvent(event.id),
      ]);

      const allVols    = volRes.data.volunteers||[];
      const evVols     = allVols.filter(v=>v.eventId===event.id||v.eventName===event.name);
      const venueList  = venRes.data.venues||[];
      const savedAssigns = assignRes.data.assignments||[];

      setVols(evVols);
      setVenues(venueList);

      // Build a name→id map for venue matching (fallback for old records)
      const venueByName = {};
      venueList.forEach(v => { venueByName[v.name?.toLowerCase()] = v.id; });

      // Collect unique venue IDs we need to load gates for
      const venueIdsToLoad = new Set();

      const savedMap = {};
      for(const a of savedAssigns) {
        if(!a.volunteer_id) continue;

        // Resolve venue_id: prefer stored venue_id, fall back to name match
        let venueId   = a.venue_id || "";
        let venueName = a.venue_name || "";

        if(!venueId && venueName) {
          // Fallback: find venue by name from the loaded venues list
          venueId = venueByName[venueName.toLowerCase()] || "";
        }
        if(venueId) venueIdsToLoad.add(venueId);

        savedMap[a.volunteer_id] = {
          venueId,
          venueName,
          gateId:   a.gate_id   || "",
          gateName: a.gate_name || "",
        };
      }

      setAssign(savedMap);

      // Pre-load gates for ALL venues that appear in saved assignments
      // This ensures dropdowns are populated on render
      const gateLoads = [...venueIdsToLoad].map(venueId =>
        getGatesByVenue(venueId)
          .then(r => ({ venueId, gates: r.data.gates||[] }))
          .catch(() => ({ venueId, gates: [] }))
      );
      const gateResults = await Promise.all(gateLoads);
      const newGateMap  = {};
      gateResults.forEach(({ venueId, gates }) => { newGateMap[venueId] = gates; });
      if(Object.keys(newGateMap).length > 0) {
        setGateMap(newGateMap);
      }

    } catch(e) {
      setMsg({type:"error", text:`Failed to load assignments: ${e.message}`});
    }
    setLoading(false);
  }, [event.id, event.name]); // eslint-disable-line

  useEffect(()=>{ loadData(); },[loadData, version]);

  const loadGates = async (venueId) => {
    if(gateMap[venueId]) return;
    try {
      const r = await getGatesByVenue(venueId);
      setGateMap(m=>({...m,[venueId]:r.data.gates||[]}));
    } catch { setGateMap(m=>({...m,[venueId]:[]})); }
  };

  const loadBulkGates = async (venueId) => {
    if(!venueId) { setBulkGates([]); setBulkGate(""); return; }
    try { const r = await getGatesByVenue(venueId); setBulkGates(r.data.gates||[]); }
    catch { setBulkGates([]); }
    setBulkGate("");
  };

  const handleVenueChange = async (vol, venueId) => {
    const venue = venues.find(v=>v.id===venueId);
    const a = { venueId, venueName:venue?.name||"", gateId:"", gateName:"" };
    setAssign(prev=>({...prev,[vol.id]:a}));
    if(venueId) await loadGates(venueId);
    triggerAutoSave(vol, a);
  };

  const handleGateChange = (vol, gateId) => {
    const venueId = assignments[vol.id]?.venueId;
    const gate = (gateMap[venueId]||[]).find(g=>g.id===gateId);
    const a = { ...(assignments[vol.id]||{}), gateId, gateName:gate?.name||"" };
    setAssign(prev=>({...prev,[vol.id]:a}));
    triggerAutoSave(vol, a);
  };

  const triggerAutoSave = (vol, a) => {
    if(autoSaveTimers.current[vol.id]) clearTimeout(autoSaveTimers.current[vol.id]);
    autoSaveTimers.current[vol.id] = setTimeout(()=>doSave(vol,a), 600);
  };

  const doSave = async (vol, a) => {
    if(!a?.venueId) return;
    setSaving(prev=>({...prev,[vol.id]:true}));
    try {
      await saveAssignment({
        volunteerId:   vol.id,
        volunteerName: vol.fullName,
        phone:         vol.whatsappNumber||vol.phoneNumber,
        eventId:       event.id,
        venueId:       a.venueId   || "",   // ← critical for state restore
        venueName:     a.venueName || "",
        gateId:        a.gateId    || "",
        gateName:      a.gateName  || "",
        sessionName:   a.gateName  || "",
        sessionId:     a.gateId    || "",
        date,
      });
      setAutoSaved(prev=>({...prev,[vol.id]:true}));
      setTimeout(()=>setAutoSaved(prev=>({...prev,[vol.id]:false})),2000);
    } catch { setMsg({type:"error",text:`Failed to save for ${vol.fullName}.`}); setTimeout(()=>setMsg(null),3000); }
    setSaving(prev=>({...prev,[vol.id]:false}));
  };

  const getGateCount = (gateId) => Object.values(assignments).filter(a=>a.gateId===gateId).length;
  const isGateFull   = (gate)   => gate.maxVolunteers&&gate.maxVolunteers>0&&getGateCount(gate.id)>=gate.maxVolunteers;

  const handleSendWA  = async (volId) => { try { await sendWhatsAppToVolunteer(volId); setMsg({type:"success",text:"WhatsApp sent!"}); setTimeout(()=>setMsg(null),2000); } catch { setMsg({type:"error",text:"Failed."}); } };
  const handleSendSMS = async (volId) => { try { await sendSMSToVolunteer(volId); setMsg({type:"success",text:"SMS sent!"}); setTimeout(()=>setMsg(null),2000); } catch { setMsg({type:"error",text:"Failed."}); } };
  const handleBulkWA  = async () => { setBulkSending(true); try { const r=await sendWhatsAppBulk(date); setMsg({type:"success",text:r.data.message}); } catch { setMsg({type:"error",text:"Bulk WhatsApp failed."}); } setBulkSending(false); };
  const handleBulkSMS = async () => { setBulkSmsSending(true); try { const r=await sendSMSBulk(date); setMsg({type:"success",text:r.data.message}); } catch { setMsg({type:"error",text:"Bulk SMS failed."}); } setBulkSmsSending(false); };

  const toggleSelect   = (id) => setSelected(prev=>{ const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; });
  const selectAllVisible = () => setSelected(new Set(displayList.map(v=>v.id)));
  const clearSel       = () => setSelected(new Set());

  // ── EXPORT SELECTED RECORDS (CSV or XLSX) ────────────────────
  // Both formats share the same column layout and row content;
  // only the file encoding differs:
  //   • CSV  — plain text, opens in any spreadsheet app
  //   • XLSX — native Excel binary, opens with Excel's icon
  //
  // The xlsx library is loaded lazily (only when the user actually
  // clicks Export Excel) to keep the initial bundle small.

  // Build the table data once — reused by both formats.
  const buildExportRows = () => {
    const headers = [
      "Full Name", "ITS ID", "Phone", "Email", "Jamiat", "Jamaat",
      "Designation", "Zone", "Coordinator",
      "Venue", "Gate", "Assignment Date",
      "Registration Status",
    ];
    const rows = volunteers
      .filter(v => selected.has(v.id))
      .map(v => {
        const a = assignments[v.id] || {};
        return [
          v.fullName || "", v.itsId || "", v.whatsappNumber || v.phoneNumber || "",
          v.email || "", v.jamiat || "", v.jamaat || "",
          v.designation || "", v.zone || "", v.coordinator || "",
          a.venueName || "", a.gateName || "", date || "",
          v.registrationStatus || "",
        ];
      });
    return { headers, rows };
  };

  // Filename without extension, slugified from event name + date
  const buildExportFilename = () => {
    const stamp = new Date().toISOString().split("T")[0];
    const slug  = event.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    return `duty-assignments-${slug}-${stamp}`;
  };

  // Browser download helper — works for both Blob types
  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a   = document.createElement("a");
    a.href     = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── CSV export ──
  const exportSelectedCSV = () => {
    if (selected.size === 0) return;
    const { headers, rows } = buildExportRows();

    const csvEscape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const csv = [
      headers.map(csvEscape).join(","),
      ...rows.map(r => r.map(csvEscape).join(",")),
    ].join("\r\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    triggerDownload(blob, `${buildExportFilename()}.csv`);

    setMsg({ type: "success", text: `Exported ${selected.size} record${selected.size > 1 ? "s" : ""} to CSV` });
    setTimeout(() => setMsg(null), 2500);
  };

  // ── Excel (.xlsx) export ──
  // Uses the SheetJS (xlsx) library, loaded on demand to avoid
  // bloating the initial JS bundle for users who never export.
  const exportSelectedExcel = async () => {
    if (selected.size === 0) return;
    try {
      // Dynamic import — webpack splits this into its own chunk
      const XLSX = await import("xlsx");
      const { headers, rows } = buildExportRows();

      // aoa_to_sheet = "array of arrays" → worksheet
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

      // Auto-fit column widths based on the longest cell in each column
      const colWidths = headers.map((h, i) => {
        const maxLen = Math.max(
          h.length,
          ...rows.map(r => String(r[i] || "").length),
        );
        return { wch: Math.min(maxLen + 2, 40) }; // cap at 40 chars
      });
      ws["!cols"] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Duty Assignments");

      // Generate file as ArrayBuffer, wrap in Blob, trigger download
      const buf  = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      triggerDownload(blob, `${buildExportFilename()}.xlsx`);

      setMsg({ type: "success", text: `Exported ${selected.size} record${selected.size > 1 ? "s" : ""} to Excel` });
      setTimeout(() => setMsg(null), 2500);
    } catch (err) {
      console.error("[Excel export] error:", err);
      setMsg({ type: "error", text: "Excel export failed. Try CSV instead." });
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const handleBulkAssign = async () => {
    if(!bulkVenue||selected.size===0) return;
    setBulkAssigning(true);
    const venue = venues.find(v=>v.id===bulkVenue);
    const gate  = bulkGates.find(g=>g.id===bulkGate);
    let done=0;
    for(const volId of selected) {
      const vol = volunteers.find(v=>v.id===volId);
      if(!vol) continue;
      const a = { venueId:bulkVenue, venueName:venue?.name||"", gateId:bulkGate, gateName:gate?.name||"" };
      setAssign(prev=>({...prev,[volId]:a}));
      try {
        await saveAssignment({ volunteerId:vol.id, volunteerName:vol.fullName, phone:vol.whatsappNumber||vol.phoneNumber, eventId:event.id, venueId:bulkVenue, venueName:venue?.name||"", gateId:bulkGate, gateName:gate?.name||"", sessionName:gate?.name||"", sessionId:bulkGate, date });
        done++;
      } catch {}
    }
    setMsg({type:"success",text:`Assigned ${done} volunteers to ${venue?.name||"venue"}${gate?` / ${gate.name}`:""}`});
    clearSel(); setShowBulk(false);
    setTimeout(()=>setMsg(null),3000);
    setBulkAssigning(false);
  };

  // ── Column sorting ────────────────────────
  const toggleSort = (col) => {
    if(sortCol===col) setSortDir(d=>d==="asc"?"desc":"asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  const VOL_FILTER_FIELDS = [
    {key:"fullName",           label:"Full Name",    type:"text"},
    {key:"itsId",              label:"ITS ID",       type:"text"},
    {key:"jamiat",             label:"Jamiat",       type:"text"},
    {key:"jamaat",             label:"Jamaat",       type:"text"},
    {key:"designation",        label:"Designation",  type:"text"},
    {key:"zone",               label:"Zone",         type:"text"},
    {key:"coordinator",        label:"Coordinator",  type:"text"},
    {key:"email",              label:"Email",        type:"text"},
    {key:"whatsappNumber",     label:"WhatsApp",     type:"text"},
    {key:"phoneNumber",        label:"Phone",        type:"text"},
    {key:"registrationStatus", label:"Status",       type:"select", options:["pending","arrived","not_coming"]},
    {key:"arrivalDate",        label:"Arrival Date", type:"date"},
    {key:"departureDate",      label:"Departure Date",type:"date"},
    {key:"age",                label:"Age",          type:"text"},
  ];

  const getVolVal = (v,key) => String(v[key]||"");

  const assigned = volunteers.filter(v=>assignments[v.id]?.venueId).length;

  const displayList = applyFilters(
    [...volunteers]
      .sort((a,b)=>{
        const va=String(a[sortCol]||"").toLowerCase();
        const vb=String(b[sortCol]||"").toLowerCase();
        return sortDir==="asc"?va.localeCompare(vb):vb.localeCompare(va);
      })
      .filter(v=>{
        const q=search.toLowerCase();
        const mSearch=!q||v.fullName?.toLowerCase().includes(q)||v.itsId?.includes(q)||v.whatsappNumber?.includes(q)||v.jamiat?.toLowerCase().includes(q)||v.jamaat?.toLowerCase().includes(q)||v.email?.toLowerCase().includes(q);
        const mAssign=filter==="All"||(filter==="Assigned"&&assignments[v.id]?.venueId)||(filter==="Unassigned"&&!assignments[v.id]?.venueId);
        return mSearch&&mAssign;
      }),
    filterRows, filterLogic, getVolVal
  );

  const selStyle = { width:"100%", padding:"7px 10px", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:8, color:C.text, fontSize:12, outline:"none", cursor:"pointer" };

  const SortHeader = ({ col, label }) => (
    <th onClick={()=>toggleSort(col)} style={{ padding:"10px 12px", textAlign:"left", fontSize:10, fontWeight:700, color:sortCol===col?C.accent:C.muted, letterSpacing:"0.7px", textTransform:"uppercase", borderBottom:`1px solid ${C.border}`, whiteSpace:"nowrap", cursor:"pointer", userSelect:"none" }}>
      {label} <span style={{ fontSize:9 }}>{sortCol===col?(sortDir==="asc"?"▲":"▼"):"▲"}</span>
    </th>
  );
  const PlainHeader = ({ label }) => (
    <th style={{ padding:"10px 12px", textAlign:"left", fontSize:10, fontWeight:700, color:C.muted, letterSpacing:"0.7px", textTransform:"uppercase", borderBottom:`1px solid ${C.border}`, whiteSpace:"nowrap" }}>{label}</th>
  );

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:20, fontSize:13 }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:C.accent, cursor:"pointer", fontWeight:600, padding:0 }}>← Events</button>
        <span style={{ color:C.muted }}>›</span>
        <span style={{ color:C.text, fontWeight:700 }}>{event.name}</span>
        <span style={{ color:C.muted }}>›</span>
        <span style={{ color:C.text, fontWeight:700 }}>Assign Duties</span>
      </div>

      {/* Header */}
      <div className="flex-between" style={{ marginBottom:18, flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:800, color:C.text, fontFamily:"Georgia, serif" }}>Assign Duties</div>
          <div style={{ fontSize:13, color:C.muted, marginTop:3 }}>
            {assigned}/{volunteers.length} assigned · <strong style={{color:C.accent}}>{event.name}</strong>
          </div>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          <button onClick={handleBulkWA} disabled={bulkSending||assigned===0}
            style={{ padding:"9px 14px", background:assigned>0?C.orangeSoft:C.border, border:`1px solid ${assigned>0?C.orange:C.border}`, borderRadius:10, color:assigned>0?C.orange:C.muted, fontSize:12, fontWeight:600, cursor:assigned>0?"pointer":"not-allowed", whiteSpace:"nowrap" }}>
            {bulkSending?"Sending...":"💬 WhatsApp All"}
          </button>
          <button onClick={handleBulkSMS} disabled={bulkSmsSending||assigned===0}
            style={{ padding:"9px 14px", background:assigned>0?C.accentSoft:C.border, border:`1px solid ${assigned>0?C.accent:C.border}`, borderRadius:10, color:assigned>0?C.accent:C.muted, fontSize:12, fontWeight:600, cursor:assigned>0?"pointer":"not-allowed", whiteSpace:"nowrap" }}>
            {bulkSmsSending?"Sending...":"📱 SMS All"}
          </button>
        </div>
      </div>

      {/* Search + filter row */}
      <div style={{ display:"flex", gap:10, marginBottom:10, flexWrap:"wrap", alignItems:"flex-end" }}>
        <div>
          <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Date</div>
          <input type="date" value={date} onChange={e=>setDate(e.target.value)}
            style={{ padding:"9px 13px", background:C.card, border:`1px solid ${C.accent}`, borderRadius:9, color:C.accent, fontSize:13, outline:"none", fontWeight:700 }} />
        </div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name, ITS ID, WhatsApp, email..."
          style={{ flex:1, minWidth:180, padding:"9px 13px", background:C.card, border:`1px solid ${C.border}`, borderRadius:9, color:C.text, fontSize:13, outline:"none" }} />
        <div style={{ display:"flex", gap:6 }}>
          {["All","Assigned","Unassigned"].map(f=>(
            <button key={f} onClick={()=>setFilter(f)}
              style={{ padding:"8px 14px", fontSize:12, fontWeight:600, cursor:"pointer", background:filter===f?C.accent:C.card, color:filter===f?"#000":C.muted, border:`1px solid ${filter===f?C.accent:C.border}`, borderRadius:8 }}>
              {f}
            </button>
          ))}
        </div>
        <button onClick={()=>setShowFilters(v=>!v)}
          style={{ padding:"8px 14px", fontSize:12, fontWeight:600, cursor:"pointer", background:filterRows.length>0||showFilters?C.accentSoft:C.card, color:filterRows.length>0?C.accent:C.muted, border:`1px solid ${filterRows.length>0?C.accent:C.border}`, borderRadius:8 }}>
          Field Filters{filterRows.length>0?` (${filterRows.length})`:""}
        </button>
        {filterRows.length>0&&<button onClick={()=>setFilterRows([])} style={{ padding:"8px 12px", fontSize:12, fontWeight:600, cursor:"pointer", background:C.redSoft, color:C.red, border:`1px solid ${C.red}33`, borderRadius:8 }}>Clear</button>}
      </div>

      {/* Dynamic filter builder with AND/OR */}
      {showFilters&&<FilterBuilder fields={VOL_FILTER_FIELDS} rows={filterRows} onChange={setFilterRows} logic={filterLogic} onLogicChange={setFilterLogic} C={C} />}
      {filterRows.length>0&&<div style={{ fontSize:12, color:C.muted, marginBottom:8 }}>Showing <strong style={{color:C.text}}>{displayList.length}</strong> of <strong style={{color:C.text}}>{volunteers.length}</strong> volunteers</div>}

      <Msg msg={msg} C={C} />

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:14 }}>
        {[{label:"Total",value:volunteers.length,color:C.accent},{label:"Assigned",value:assigned,color:C.green},{label:"Unassigned",value:volunteers.length-assigned,color:C.orange}].map((s,i)=>(
          <div key={i} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:11, padding:"11px 14px" }}>
            <div style={{ fontSize:20, fontWeight:800, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:10, color:C.muted, fontWeight:600, textTransform:"uppercase", marginTop:2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Bulk Assignment Panel */}
      {showBulk&&(
        <div style={{ background:C.card, border:`1px solid ${C.accent}44`, borderRadius:12, padding:16, marginBottom:14 }}>
          <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:12 }}>Bulk Assign {selected.size} Selected Volunteers</div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"flex-end" }}>
            <div style={{ flex:1, minWidth:160 }}>
              <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Venue</div>
              <select value={bulkVenue} onChange={e=>{ setBulkVenue(e.target.value); loadBulkGates(e.target.value); }}
                style={{ width:"100%", padding:"9px 12px", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:8, color:C.text, fontSize:13, outline:"none", cursor:"pointer" }}>
                <option value="">Select Venue...</option>
                {venues.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div style={{ flex:1, minWidth:160 }}>
              <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Gate (optional)</div>
              <select value={bulkGate} onChange={e=>setBulkGate(e.target.value)} disabled={!bulkVenue}
                style={{ width:"100%", padding:"9px 12px", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:8, color:C.text, fontSize:13, outline:"none", cursor:bulkVenue?"pointer":"not-allowed" }}>
                <option value="">No specific gate</option>
                {bulkGates.map(g=><option key={g.id} value={g.id}>{g.name}{g.maxVolunteers?` (Max: ${g.maxVolunteers})`:""}</option>)}
              </select>
            </div>
            <button onClick={handleBulkAssign} disabled={bulkAssigning||!bulkVenue}
              style={{ padding:"9px 20px", background:bulkVenue?C.green:C.border, border:"none", borderRadius:9, color:bulkVenue?"#000":C.muted, fontSize:13, fontWeight:700, cursor:bulkVenue?"pointer":"not-allowed" }}>
              {bulkAssigning?"Assigning...":"Assign All"}
            </button>
            <button onClick={()=>setShowBulk(false)} style={{ padding:"9px 14px", background:"none", border:`1px solid ${C.border}`, borderRadius:9, color:C.muted, fontSize:13, cursor:"pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Select controls */}
      <div style={{ display:"flex", gap:8, marginBottom:10, alignItems:"center", flexWrap:"wrap" }}>
        <button onClick={selectAllVisible} style={{ padding:"6px 12px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:7, color:C.accent, fontSize:12, fontWeight:600, cursor:"pointer" }}>
          Select All ({displayList.length})
        </button>
        <button onClick={clearSel} style={{ padding:"6px 12px", background:"none", border:`1px solid ${C.border}`, borderRadius:7, color:C.muted, fontSize:12, cursor:"pointer" }}>Clear</button>
        <span style={{ fontSize:12, color:C.text, fontWeight:600 }}>{selected.size} selected</span>
        {selected.size>0&&(
          <>
            <button onClick={()=>setShowBulk(true)}
              style={{ padding:"6px 14px", background:C.greenSoft, border:`1px solid ${C.green}44`, borderRadius:7, color:C.green, fontSize:12, fontWeight:700, cursor:"pointer" }}>
              Bulk Assign {selected.size}
            </button>
            <button onClick={exportSelectedCSV}
              title="Export selected records to CSV"
              style={{ padding:"6px 14px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:7, color:C.accent, fontSize:12, fontWeight:700, cursor:"pointer" }}>
              ⬇ CSV ({selected.size})
            </button>
            <button onClick={exportSelectedExcel}
              title="Export selected records to Excel (.xlsx)"
              style={{ padding:"6px 14px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:7, color:C.accent, fontSize:12, fontWeight:700, cursor:"pointer" }}>
              ⬇ Excel ({selected.size})
            </button>
          </>
        )}
      </div>

      {/* Table */}
      {loading ? <div style={{ padding:40, textAlign:"center", color:C.muted }}>Loading volunteers...</div>
      : volunteers.length===0 ? (
        <div style={{ padding:60, textAlign:"center", background:C.card, border:`1px solid ${C.border}`, borderRadius:16 }}>
          <div style={{ fontSize:40, marginBottom:14 }}>👥</div>
          <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:6 }}>No volunteers for this event</div>
          <div style={{ fontSize:13, color:C.muted }}>Go to Events → click the event → Volunteers tab → Add Volunteers first.</div>
        </div>
      ) : displayList.length===0 ? (
        <div style={{ padding:40, textAlign:"center", color:C.muted, background:C.card, border:`1px solid ${C.border}`, borderRadius:16 }}>No volunteers match the current filters.</div>
      ) : (
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, overflow:"hidden" }}>
          <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, fontSize:12, color:C.muted }}>
            {displayList.length} of {volunteers.length} volunteers · Click column headers to sort
          </div>
          <div className="responsive-table">
            <table>
              <thead>
                <tr style={{ background:C.bg }}>
                  <PlainHeader label="" />
                  <SortHeader col="fullName"           label="Name" />
                  <SortHeader col="itsId"              label="ITS ID" />
                  <SortHeader col="whatsappNumber"     label="WhatsApp" />
                  <SortHeader col="phoneNumber"        label="Phone" />
                  <SortHeader col="email"              label="Email" />
                  <SortHeader col="jamiat"             label="Jamiat" />
                  <SortHeader col="jamaat"             label="Jamaat" />
                  <SortHeader col="designation"        label="Designation" />
                  <SortHeader col="zone"               label="Zone" />
                  <SortHeader col="arrivalDate"        label="Arrival" />
                  <SortHeader col="departureDate"      label="Departure" />
                  <SortHeader col="registrationStatus" label="Status" />
                  <PlainHeader label="Venue" />
                  <PlainHeader label="Gate" />
                  <PlainHeader label="💾" />
                  <PlainHeader label="WA" />
                  <PlainHeader label="SMS" />
                </tr>
              </thead>
              <tbody>
                {displayList.map((vol,i) => {
                  const a = assignments[vol.id]||{};
                  const isAssigned = !!a.venueId;
                  const venueGates = a.venueId?(gateMap[a.venueId]||[]):[];
                  const isSaving   = saving[vol.id];
                  const wasSaved   = autoSaved[vol.id];
                  const isSel      = selected.has(vol.id);
                  return (
                    <tr key={i} style={{ borderBottom:`1px solid ${C.border}`, transition:"background 0.15s", background:isSel?`${C.accent}11`:"transparent" }}
                      onMouseEnter={e=>!isSel&&(e.currentTarget.style.background=C.border+"44")}
                      onMouseLeave={e=>!isSel&&(e.currentTarget.style.background="transparent")}>
                      <td style={{ padding:"10px 12px" }} onClick={e=>{ e.stopPropagation(); toggleSelect(vol.id); }}>
                        <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${isSel?C.accent:C.border}`, background:isSel?C.accent:"transparent", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
                          {isSel&&<span style={{ color:"#000", fontSize:11, fontWeight:800 }}>v</span>}
                        </div>
                      </td>
                      <td style={{ padding:"10px 12px" }}><div style={{ fontSize:13, color:C.text, fontWeight:700, whiteSpace:"nowrap" }}>{vol.fullName}</div></td>
                      <td style={{ padding:"10px 12px", fontSize:11, color:C.muted, fontFamily:"monospace", whiteSpace:"nowrap" }}>{vol.itsId||"—"}</td>
                      <td style={{ padding:"10px 12px", fontSize:11, color:C.green, fontWeight:600, whiteSpace:"nowrap" }}>{vol.whatsappNumber||"—"}</td>
                      <td style={{ padding:"10px 12px", fontSize:11, color:C.muted, whiteSpace:"nowrap" }}>{vol.phoneNumber||"—"}</td>
                      <td style={{ padding:"10px 12px", fontSize:11, color:C.muted }}>{vol.email||"—"}</td>
                      <td style={{ padding:"10px 12px", fontSize:11, color:C.text }}>{vol.jamiat||"—"}</td>
                      <td style={{ padding:"10px 12px", fontSize:11, color:C.text }}>{vol.jamaat||"—"}</td>
                      <td style={{ padding:"10px 12px" }}>{vol.designation?<Badge text={vol.designation} color={C.purple}/>:<span style={{fontSize:11,color:C.muted}}>—</span>}</td>
                      <td style={{ padding:"10px 12px", fontSize:11, color:C.text }}>{vol.zone||"—"}</td>
                      <td style={{ padding:"10px 12px", fontSize:11, color:C.accent, fontWeight:600 }}>{vol.arrivalDate||"—"}</td>
                      <td style={{ padding:"10px 12px", fontSize:11, color:C.orange, fontWeight:600 }}>{vol.departureDate||"—"}</td>
                      <td style={{ padding:"10px 12px" }}><Badge text={regLabel(vol.registrationStatus)} color={regColor(vol.registrationStatus,C)} /></td>
                      <td style={{ padding:"10px 12px", minWidth:150 }}>
                        <select value={a.venueId||""} onChange={e=>handleVenueChange(vol,e.target.value)}
                          style={{ ...selStyle, border:`1px solid ${a.venueId?C.green:C.border}` }}>
                          <option value="">Select Venue...</option>
                          {venues.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
                        </select>
                      </td>
                      <td style={{ padding:"10px 12px", minWidth:150 }}>
                        {!a.venueId?<div style={{ fontSize:11, color:C.muted, fontStyle:"italic" }}>Select venue first</div>
                        :venueGates.length===0?<div style={{ fontSize:11, color:C.orange }}>No gates</div>:(
                          <>
                            <select value={a.gateId||""} onChange={e=>handleGateChange(vol,e.target.value)}
                              style={{ ...selStyle, border:`1px solid ${a.gateId?C.accent:C.border}` }}>
                              <option value="">Select Gate...</option>
                              {venueGates.map(g=>{
                                const full=isGateFull(g)&&g.id!==a.gateId;
                                return <option key={g.id} value={g.id} disabled={full}>{g.name}{full?" (Full)":(g.maxVolunteers?` (Max:${g.maxVolunteers})`:"")} </option>;
                              })}
                            </select>
                            {a.gateId&&(()=>{
                              const gate=venueGates.find(g=>g.id===a.gateId);
                              if(!gate?.maxVolunteers) return null;
                              const count=getGateCount(a.gateId);
                              const pct=Math.min(100,Math.round(count/gate.maxVolunteers*100));
                              return <div style={{ marginTop:4 }}><div style={{ background:C.border, borderRadius:99, height:4 }}><div style={{ width:`${pct}%`, background:pct>=100?C.red:pct>=80?C.orange:C.green, borderRadius:99, height:"100%", transition:"width 0.3s" }} /></div><div style={{ fontSize:9, color:pct>=100?C.red:C.muted, marginTop:2 }}>{count}/{gate.maxVolunteers}</div></div>;
                            })()}
                          </>
                        )}
                      </td>
                      <td style={{ padding:"10px 12px", textAlign:"center" }}>
                        {isSaving?<span style={{fontSize:13}}>⏳</span>:wasSaved?<span style={{fontSize:13,color:C.green,fontWeight:700}}>✅</span>:isAssigned?<span style={{fontSize:13,color:C.muted}}>✓</span>:<span style={{fontSize:11,color:C.muted}}>—</span>}
                      </td>
                      <td style={{ padding:"10px 12px" }}>
                        {isAssigned?<button onClick={()=>handleSendWA(vol.id)} style={{ padding:"5px 8px", background:C.orangeSoft, border:`1px solid ${C.orange}44`, borderRadius:7, color:C.orange, fontSize:11, fontWeight:700, cursor:"pointer" }}>💬</button>:<span style={{fontSize:11,color:C.muted}}>—</span>}
                      </td>
                      <td style={{ padding:"10px 12px" }}>
                        {isAssigned?<button onClick={()=>handleSendSMS(vol.id)} style={{ padding:"5px 8px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:7, color:C.accent, fontSize:11, fontWeight:700, cursor:"pointer" }}>📱</button>:<span style={{fontSize:11,color:C.muted}}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// MAIN
// ============================================
export default function DutyAssignment() {
  const { C } = useTheme();
  const [selectedEvent, setEvent] = useState(null);
  if(selectedEvent) return <AssignView event={selectedEvent} onBack={()=>setEvent(null)} C={C} />;
  return <EventList onSelect={setEvent} C={C} />;
}